import type { TaskMenuTarget } from '@/src/renderer/features/taskManagement/components/TaskMenu/TaskMenu.types'
import type { TaskMenuMarks } from '@/src/renderer/features/taskManagement/components/TaskMenu/taskMenuMarks'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { useDndContext, useDroppable } from '@dnd-kit/core'
import { TaskEntry } from '../../../../components/TaskEntry'
import type { TaskCreationData } from '../../../../components/TaskEntry'
import type { Task } from '../../../../../../lib/db/models'
import { taskToEntryData } from '../../../../utils/taskToEntryData'
import { Dot } from '../../../../../../components/ui/Dot'
import { dateKey } from '../../hooks/upcomingTasks.utils'
import { DraggableTaskEntry } from '../DraggableTaskEntry'
import { SelectionHull, useSelectionHullResting } from '../../../../components/SelectionHull'
import './DayGroup.scss'

export interface DayGroupProps {
  date: Date
  tasks: Task[]
  /** Day-key of today (from the parent, driven by useMidnightRefresh). When
   *  this matches `dateKey(date)`, the day number uses the accent colour. */
  todayDateKey: string
  /** ID of the task currently in expanded/edit mode — ONLY for this day.
   *  Parent filters: other days receive `null` so their memo hits. */
  editingTaskId: string | null
  /** Die ausgewählten Aufgaben DIESES Tages. Tage ohne Auswahl bekommen alle
   *  dieselbe geteilte leere Menge, damit `React.memo` hier greift. */
  selectedIds: ReadonlySet<string>
  /** True when this specific day is in "create new task" mode. */
  isCreating: boolean
  onStartEdit: (taskId: string) => void
  onCancelEdit: () => void
  onNavigateEdit: (taskId: string, direction: 'next' | 'prev') => void
  onSelectTask: (taskId: string, e: React.MouseEvent) => void
  onStartCreate: (dateKey: string) => void
  onCancelCreate: () => void
  onSubmitCreate: (dateKey: string, data: TaskCreationData) => void
  onToggleComplete: (taskId: string) => void
  onDelete: (taskId: string) => void
  onUpdateTask: (taskId: string, data: TaskCreationData) => void
  onDuplicate?: (taskId: string) => void
  /** „Im Projekt zeigen“ im Rechtsklick-Menü (#257). */
  onShowInProject?: (taskId: string) => void
  onContextMenuBadgeUpdate?: (clickedTaskId: string, updates: Partial<Omit<Task, 'id'>>) => void
  getContextMenuMarks?: (clickedTaskId: string) => TaskMenuMarks
  /**
   * **Play** — beginnt einen **Fokus** an dieser Aufgabe (Kürzel `P`).
   * Fehlt er, tut der Play-Knopf der Zeile nichts.
   */
  onPlay?: (taskId: string) => void
  /** Läuft gerade ein Zug? Schaltet die Übergänge an den Zeilen scharf. */
  isDragActive?: boolean
  menuShortcut?: { taskId: string; target: TaskMenuTarget } | null
  onMenuShortcutClose?: () => void
}


/**
 * Tiny isolation wrapper for `useDroppable`.
 *
 * The dnd-kit `useDroppable` hook subscribes to DndContext, and DndContext
 * fires updates on *every* cursor move during a drag. Hosting that hook
 * directly inside DayGroup made every DndContext update bypass the outer
 * React.memo and re-render the entire DayGroup tree (TaskEntries, framer-
 * motion, badge dropdowns) for every droppable on the page (~120 of them
 * across 4 visible months). Profiling showed ~1200 DayGroup re-renders per
 * single drag-and-drop operation.
 *
 * Splitting the droppable into its own component means DndContext updates
 * only re-render this thin wrapper, while the heavy DayGroup body stays
 * memoized.
 */
/** Abstand zwischen zwei Zeilen (`.day-group__tasks { gap: 2px }`). */
const ROW_GAP_PX = 2

interface DayDropZoneProps {
  dayKey: string
  className: string
  /** Die Zeilen DIESES Tages — für die Frage, ob der Zug von hier kommt. */
  taskIds: ReadonlySet<string>
  children: React.ReactNode
}

const DayDropZone: React.FC<DayDropZoneProps> = ({ dayKey, className, taskIds, children }) => {
  const { setNodeRef } = useDroppable({ id: dayKey })
  /*
    **Nur der ZIEL-Tag färbt sich** (Nutzer, 22.09.2026). Bis dahin tönte sich
    jeder Tag unter dem Zeiger, auch der, aus dem gezogen wurde — dort ist es
    aber kein Ablegen, sondern ein Nichts, und die Fläche versprach etwas, das
    nicht passiert.

    Gefragt wird NICHT `isOver` vom eigenen Droppable: Liegen Zeilen im Tag,
    meldet die Treffer-Erkennung die Zeile, nicht den Tag — der Tag leuchtete
    dann nie. Also: Gehört das gemeldete Ziel zu diesem Tag (der Tag selbst
    oder eine seiner Zeilen)? Und kommt der Zug nicht von hier?

    `useDndContext` kostet hier nichts: Diese Hülle rendert wegen
    `useDroppable` ohnehin bei jedem Zug-Schritt neu — genau dafür steht sie
    getrennt vom schweren DayGroup-Rumpf (siehe Kommentar oben).
  */
  const { active, over } = useDndContext()
  const overId = over ? String(over.id) : null
  const istZiel = overId !== null && (overId === dayKey || taskIds.has(overId))
  const istQuelle = active != null && taskIds.has(String(active.id))
  const nimmtAuf = istZiel && !istQuelle

  /*
    **Der Ziel-Tag macht Platz, bevor die Zeile kommt** (Nutzer, 22.09.2026).

    Ohne das passierte zweierlei: Ein leerer Tag zeigte gar keine Lücke, man
    legte ins Nichts ab. Und ein Tag mit Zeilen schob seine letzte zwar
    beiseite — aber aus der getönten Fläche heraus, weil der Kasten des Tages
    nicht mitwuchs. Das sah nach Fehler aus.

    Reserviert wird die Höhe der gezogenen Zeile plus den Reihenabstand, also
    genau, was die Strategie an Verschiebung erzeugt. Das Maß kommt von
    dnd-kit (`initial` = beim Anfassen gemessen) und steht damit fest, während
    der Zug läuft.
  */
  const zeilenhoehe = active?.rect.current.initial?.height ?? 0
  const platz = nimmtAuf ? zeilenhoehe + ROW_GAP_PX : 0

  return (
    <div
      ref={setNodeRef}
      className={`${className}${nimmtAuf ? ' day-group--drop-over' : ''}`}
      data-date={dayKey}
      style={{ '--day-drop-room': `${platz}px` } as React.CSSProperties}
    >
      {children}
    </div>
  )
}

/**
 * One day in the Upcoming list: day-of-month + weekday label + task rows for
 * that date. Memoized so expand/collapse in a different day doesn't ripple
 * through every DayGroup on the page.
 */
const DayGroupComponent: React.FC<DayGroupProps> = ({
  date,
  tasks,
  todayDateKey,
  editingTaskId,
  selectedIds,
  isCreating,
  onStartEdit,
  onCancelEdit,
  onNavigateEdit,
  onSelectTask,
  onStartCreate,
  onCancelCreate,
  onSubmitCreate,
  onToggleComplete,
  onDelete,
  onUpdateTask,
  onDuplicate,
  onShowInProject,
  onContextMenuBadgeUpdate,
  getContextMenuMarks,
  isDragActive,
  menuShortcut, onMenuShortcutClose, onPlay,
}) => {
  const { i18n } = useTranslation()
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : 'en-US'

  const entryData = useMemo(() => tasks.map(taskToEntryData), [tasks])
  const weekday = useMemo(
    () => date.toLocaleDateString(locale, { weekday: 'long' }),
    [date, locale],
  )
  const dayKey = dateKey(date)
  const isToday = dayKey === todayDateKey
  // Stabile Menge: Die Hülle vergleicht damit bei jedem Zug-Schritt, ob die
  // gezogene Zeile aus diesem Tag stammt.
  const dayTaskIds = useMemo(() => new Set(tasks.map(t => t.id)), [tasks])
  // Für die Auswahl-Hülle (#293): die Zeilen in ihrer Reihenfolge und die ruhenden darunter.
  const rowIds = useMemo(() => tasks.map(t => t.id), [tasks])
  const scopeResting = useSelectionHullResting()
  const dormantIds = useMemo(() => new Set(tasks.filter(t => t.pausedAt != null).map(t => t.id)), [tasks])

  // Stable callback for TaskEntry.onSubmitEdit — inline in JSX would create
  // a new reference every render and defeat TaskEntry's React.memo.
  const handleSubmitEdit = useCallback(
    (id: string, data: TaskCreationData) => {
      onUpdateTask(id, data)
      onCancelEdit()
    },
    [onUpdateTask, onCancelEdit],
  )

  const handleStartCreate = useCallback(() => {
    onStartCreate(dayKey)
  }, [onStartCreate, dayKey])

  const handleSubmitCreate = useCallback(
    (data: TaskCreationData) => {
      onSubmitCreate(dayKey, data)
    },
    [onSubmitCreate, dayKey],
  )

  return (
    <DayDropZone
      dayKey={dayKey}
      taskIds={dayTaskIds}
      className={`day-group${isToday ? ' day-group--today' : ''}`}
    >
      <div className="day-group__header">
        <span className="day-group__day">{date.getDate()}</span>
        <Dot className="day-group__dot" />
        <span className="day-group__weekday">{weekday}</span>
        {/* Hover-only "+" affordance: click opens an inline TaskEntry
            creator for this day (startDate pre-filled). Hidden while a
            creator for this day is already open. */}
        {!isCreating && (
          <button
            type="button"
            className="day-group__add"
            onClick={handleStartCreate}
            aria-label="Aufgabe an diesem Tag hinzufügen"
          >
            <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      {/* Always render the container so AnimatePresence stays mounted when
          the LAST task of the day exits. Without this wrapper mount/unmount
          cycle would cut the exit animation short. Empty state is handled
          via SCSS `:not(:empty)` so no stray padding leaks through. */}
      <div className="day-group__tasks">
        {/* Nur mit Zeilen: Ein leerer Tag bleibt `:empty` und ohne Abstand nach oben (SCSS). */}
        {tasks.length > 0 && (
          <SelectionHull
            rowIds={rowIds}
            selectedIds={selectedIds}
            dormantIds={dormantIds}
            excludedId={editingTaskId}
            rowAttribute="data-task-id"
            rowSelector=".task-entry"
            suspended={isDragActive}
          />
        )}
        {/* Exit: height collapse + opacity fade, with `marginBottom: -2`
            cancelling the flex-gap to the next sibling so there's no
            unmount-time snap. No `layout` prop on the row — framer-motion
            would otherwise run layout animations on newly mounted rows
            when the user switches months, making them slide in from the
            top. */}
        <AnimatePresence initial={false}>
          {entryData.map(task => {
              const isSelected = selectedIds.has(task.id)
              const isEditing = editingTaskId === task.id
              return (
                <motion.div
                  key={task.id}
                  data-task-id={task.id}
                  initial={false}
                  // height collapses the row, marginBottom: -2 cancels the
                  // flex-gap to the next sibling DURING exit so the A↔C
                  // distance stays 2px throughout — no unmount-time jump.
                  // `overflow: hidden` is only applied during exit so the
                  // collapsing height doesn't spill content; at idle the
                  // wrapper stays `overflow: visible` so TaskEntry's
                  // hover/selected `box-shadow: var(--shadow-xs)` renders
                  // — otherwise the wrapper would clip it.
                  exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: -2, overflow: 'hidden' }}
                  transition={{ duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
                >
                  <DraggableTaskEntry
                    taskId={task.id}
                    mode={isEditing ? 'expanded' : 'collapsed'}
                    task={task}
                    selected={isSelected}
                    // Während eines Zugs ruht die Hülle; die Zeilen wandern per `transform` und malen sich selbst.
                    selectionHull={!isDragActive && !scopeResting}
                    // Surrounding day list already shows the absolute date, so
                    // the collapsed badge uses relative text ("In 3 Tagen")
                    // for future dates instead of the weekday abbreviation.
                    startDateLabelMode="relative"
                    // Intentionally NOT forwarding `focused` — TaskEntry's internal
                    // scrollIntoView({ block: 'nearest' }) fires whenever `focused`
                    // flips to true, which lands the row hard against the viewport
                    // edge (inside the fade overlay). The page-level keyboard
                    // handler in UpcomingPage owns scroll positioning instead.
                    focused={false}
                    onToggleComplete={onToggleComplete}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                    onShowInProject={onShowInProject}
                    onContextMenuBadgeUpdate={onContextMenuBadgeUpdate}
                    getContextMenuMarks={getContextMenuMarks}
                    isDragActive={isDragActive}
                    menuShortcut={menuShortcut}
                    onPlay={onPlay}
                    onMenuShortcutClose={onMenuShortcutClose}
                    onClick={onSelectTask}
                    onDoubleClick={onStartEdit}
                    onSubmitEdit={handleSubmitEdit}
                    onCancel={onCancelEdit}
                    // Tab / Shift+Tab in expanded mode → jump to adjacent task.
                    // Only wire these on the currently-editing row so collapsed
                    // rows keep stable undefined handlers (React.memo friendly).
                    onNavigateNext={isEditing ? () => onNavigateEdit(task.id, 'next') : undefined}
                    onNavigatePrev={isEditing ? () => onNavigateEdit(task.id, 'prev') : undefined}
                  />
                </motion.div>
              )
          })}
        </AnimatePresence>

        {/* Inline creator — only rendered when this day is the target.
            `autoFocus` puts the cursor in the title input immediately so
            the user can start typing without another click. */}
        {isCreating && (
          <div className="day-group__creator">
            <TaskEntry
              mode="creating"
              onSubmitCreate={handleSubmitCreate}
              onCancel={onCancelCreate}
              autoFocus
            />
          </div>
        )}
      </div>
    </DayDropZone>
  )
}

export const DayGroup = React.memo(DayGroupComponent)
